import { useEffect, useRef, useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import ActionPopover from "../../../../../components/popovers/actionPopover";
import useEditFormStore from "../../../../../state-management/useEditFormStore";
import HolidayModalForm from "./modal";
import {
	useDeleteHoliday,
	useGetHoliday,
	useGetHolidaySearch,
} from "../../../../../services/holiday.service";
import useUserStore from "../../../../../state-management/useUserStore";
import { HolidayDto } from "../../../../../models/holiday.dto";
import DeleteModal from "../../../../../components/modals/DeleteModal";
import { useManageTableWithStateVariables } from "../../../../../helpers/useManageTable";
import { BsUpload } from "react-icons/bs";
import UploadHolidays from "./upload";
import ExportHolidaysModal from "./modal/ExportHolidaysModal";
import { holidaysColumns, holidaysColumnVisibility } from "./tanstack-columns";
import { TableId } from "../../../../../models/table-configuration.dto";
import TanStackEmployeeTable from "../../../../../components/table/TanStackEmployeeTable";
import { useColumnVisibilityModel } from "../../../../../helpers/useColumnVisibilityModel";
import APISearchInput from "../../../../../components/search/APISearchInput";

const Holidays = () => {
	const { currentUser } = useUserStore();
	const { formDataValues, setFormData } = useEditFormStore();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [openExportModal, setOpenExportModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [openUploadModal, setOpenUploadModal] = useState(false);
	const { columnVisibilityModel, setColumnVisibilityModel } =
		useColumnVisibilityModel(holidaysColumnVisibility);

	const { mutate: searchHoliday } = useGetHolidaySearch();

	const {
		tableAPIRef,
		defaultTableData,
		tableData,
		isLoading,
		pageCount,
		setTableData,
		setSearching,
		reloadData,
		addNewData,
	} = useManageTableWithStateVariables<HolidayDto>({
		fetchHook: useGetHoliday,
		options: {
			organizationId: currentUser.user.organizationId,
			orderBy: undefined,
		},
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteHoliday } = useDeleteHoliday();

	const handleDeleteMethod = () => {
		setDeleting(true);

		deleteHoliday(
			{ id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	return (
		<div>
			<h2 className="text-2sm font-semibold mb-3">Holidays</h2>
			<hr />
			<div className="flex items-center justify-between mt-7 mb-8">
				<APISearchInput
					fallbackData={defaultTableData}
					setTableData={setTableData}
					mutate={searchHoliday}
					setSearching={setSearching}
					popupTable
				/>
				<div className="flex items-center gap-4 ml-auto">
					<div
						className="flex items-center text-primary-blue text-sm cursor-pointer w-fit"
						onClick={() => setOpenUploadModal(true)}
					>
						<BsUpload className="mr-1 text-sm" />
						Upload Holidays
					</div>
					<div
						className="flex items-center text-primary-blue text-sm cursor-pointer w-fit"
						onClick={handleOpenModal}
					>
						<AiOutlinePlus className="mr-1 text-sm" />
						Add New Holiday
					</div>
				</div>
			</div>

			<TanStackEmployeeTable
				ref={tableAPIRef}
				tableId={TableId.HOLIDAYS}
				data={tableData}
				columns={holidaysColumns}
				columnVisibility={columnVisibilityModel}
				onColumnVisibilityChange={setColumnVisibilityModel}
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={holidaysColumnVisibility}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>

			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={setOpenModal}
			/>

			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.name}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>

			{openModal && (
				<HolidayModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={isEditForm ? "Update Holiday" : "Add New Holiday"}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					addNewData={reloadData}
				/>
			)}

			{openUploadModal && (
				<UploadHolidays
					open={openUploadModal}
					setOpen={setOpenUploadModal}
					reloadData={reloadData}
					addNewData={reloadData}
				/>
			)}
		</div>
	);
};

export default Holidays;
